/**
 * Unit interest tracking utilities
 * Tracks which units leads are viewing/interested in for better lead qualification
 */

import { supabase } from '@/integrations/supabase/client';
import { getLeadSourceData } from './lead-source';
import type { SupabaseError } from '@/types/supabase';
import type { Json } from '@/integrations/supabase/types';

export interface UnitInterestRecord {
  id: string;
  unit_id: string;
  contact_email: string;
  contact_phone?: string | null;
  lead_source: Json | null;
  engagement_score: number;
  first_viewed_at: string;
  last_viewed_at: string;
  view_count: number;
  click_count: number;
  form_submitted: boolean;
  meeting_scheduled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Track a unit view (when user visits unit detail page)
 * Creates or updates the interest record for this unit + contact email combination
 */
export async function trackUnitView(
  unitId: string,
  contactEmail?: string,
  contactPhone?: string
): Promise<{ success: boolean; error?: string; record?: UnitInterestRecord }> {
  try {
    // For anonymous users, we can't track by email
    // Store in sessionStorage temporarily and link later when they submit form
    if (!contactEmail) {
      // Store in sessionStorage for later linking
      if (typeof window !== 'undefined') {
        const anonymousViews = JSON.parse(
          sessionStorage.getItem('anonymous_unit_views') || '[]'
        );
        if (!anonymousViews.includes(unitId)) {
          anonymousViews.push(unitId);
          sessionStorage.setItem('anonymous_unit_views', JSON.stringify(anonymousViews));
        }
      }
      return { success: true };
    }

    // Get lead source data
    const leadSource = getLeadSourceData();

    // Check if record already exists
    const { data: existing, error: fetchError } = await supabase
      .from('unit_interest_tracking')
      .select('*')
      .eq('unit_id', unitId)
      .eq('contact_email', contactEmail)
      .maybeSingle() as { data: UnitInterestRecord | null; error: SupabaseError | null };

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 is "not found" which is fine
      throw fetchError;
    }

    if (existing) {
      // Update existing record
      const { data, error } = await supabase
        .from('unit_interest_tracking')
        .update({
          view_count: existing.view_count + 1,
          last_viewed_at: new Date().toISOString(),
          ...(contactPhone ? { contact_phone: contactPhone } : {}),
          // Merge lead source data (preserve existing, add new)
          lead_source: existing.lead_source 
            ? { ...(typeof existing.lead_source === 'object' ? existing.lead_source as Record<string, unknown> : {}), ...leadSource } as unknown as Json
            : leadSource as unknown as Json,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single() as { data: UnitInterestRecord | null; error: SupabaseError | null };

      if (error) throw error;
      return { success: true, record: data as UnitInterestRecord };
    } else {
      // Create new record
      const { data, error } = await supabase
        .from('unit_interest_tracking')
        .insert([{
          unit_id: unitId,
          contact_email: contactEmail,
          contact_phone: contactPhone || null,
          lead_source: leadSource as unknown as Json,
          view_count: 1,
          click_count: 0,
          engagement_score: 0,
          form_submitted: false,
          meeting_scheduled: false,
        }])
        .select()
        .single() as { data: UnitInterestRecord | null; error: SupabaseError | null };

      if (error) throw error;
      return { success: true, record: data as UnitInterestRecord };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error tracking unit view';
    console.error('Error tracking unit view:', error);
    return { success: false, error: errorMessage };
  }
}

/**
 * Track a unit click (when user clicks unit on map or interactive site plan)
 */
export async function trackUnitClick(
  unitId: string,
  contactEmail?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!contactEmail) {
      // For anonymous users, just track in sessionStorage
      if (typeof window !== 'undefined') {
        const anonymousClicks = JSON.parse(
          sessionStorage.getItem('anonymous_unit_clicks') || '[]'
        );
        if (!anonymousClicks.includes(unitId)) {
          anonymousClicks.push(unitId);
          sessionStorage.setItem('anonymous_unit_clicks', JSON.stringify(anonymousClicks));
        }
      }
      return { success: true };
    }

    // Get or create interest record
    const { data: existing } = await supabase
      .from('unit_interest_tracking')
      .select('*')
      .eq('unit_id', unitId)
      .eq('contact_email', contactEmail)
      .maybeSingle() as { data: UnitInterestRecord | null };

    if (existing) {
      // Update click count
      const { error } = await supabase
        .from('unit_interest_tracking')
        .update({
          click_count: existing.click_count + 1,
          last_viewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id);

      if (error) throw error;
    } else {
      // Create new record with click
      const leadSource = getLeadSourceData();
      const { error } = await supabase
        .from('unit_interest_tracking')
        .insert([{
          unit_id: unitId,
          contact_email: contactEmail,
          lead_source: leadSource as unknown as Json,
          view_count: 0,
          click_count: 1,
          engagement_score: 0,
          form_submitted: false,
          meeting_scheduled: false,
        }]);

      if (error) throw error;
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error tracking unit click';
    console.error('Error tracking unit click:', error);
    return { success: false, error: errorMessage };
  }
}

/**
 * Get unit interest record for a specific unit and contact email
 */
export async function getUnitInterest(
  unitId: string,
  contactEmail: string
): Promise<{ success: boolean; record?: UnitInterestRecord; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('unit_interest_tracking')
      .select('*')
      .eq('unit_id', unitId)
      .eq('contact_email', contactEmail)
      .maybeSingle() as { data: UnitInterestRecord | null; error: SupabaseError | null };

    if (error) throw error;
    return { success: true, record: data || undefined };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error getting unit interest';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update engagement score for a unit interest record
 */
export async function updateEngagementScore(
  unitId: string,
  contactEmail: string,
  score: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('unit_interest_tracking')
      .update({
        engagement_score: score,
        updated_at: new Date().toISOString(),
      })
      .eq('unit_id', unitId)
      .eq('contact_email', contactEmail);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error updating engagement score';
    return { success: false, error: errorMessage };
  }
}

/**
 * Mark form as submitted for a unit interest record
 */
export async function markFormSubmitted(
  unitId: string,
  contactEmail: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('unit_interest_tracking')
      .update({
        form_submitted: true,
        updated_at: new Date().toISOString(),
      })
      .eq('unit_id', unitId)
      .eq('contact_email', contactEmail);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error marking form submitted';
    return { success: false, error: errorMessage };
  }
}

/**
 * Mark meeting as scheduled for a unit interest record
 */
export async function markMeetingScheduled(
  unitId: string,
  contactEmail: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('unit_interest_tracking')
      .update({
        meeting_scheduled: true,
        updated_at: new Date().toISOString(),
      })
      .eq('unit_id', unitId)
      .eq('contact_email', contactEmail);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error marking meeting scheduled';
    return { success: false, error: errorMessage };
  }
}

/**
 * Link anonymous tracking to contact email after form submission
 * Transfers sessionStorage tracking data to database
 */
export async function linkAnonymousTracking(
  contactEmail: string,
  contactPhone?: string
): Promise<{ success: boolean; error?: string }> {
  if (typeof window === 'undefined') {
    return { success: true };
  }

  try {
    const anonymousViews = JSON.parse(
      sessionStorage.getItem('anonymous_unit_views') || '[]'
    ) as string[];
    const anonymousClicks = JSON.parse(
      sessionStorage.getItem('anonymous_unit_clicks') || '[]'
    ) as string[];

    const leadSource = getLeadSourceData();

    // Process views
    for (const unitId of anonymousViews) {
      await trackUnitView(unitId, contactEmail, contactPhone);
    }

    // Process clicks
    for (const unitId of anonymousClicks) {
      await trackUnitClick(unitId, contactEmail);
    }

    // Clear sessionStorage
    sessionStorage.removeItem('anonymous_unit_views');
    sessionStorage.removeItem('anonymous_unit_clicks');

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error linking anonymous tracking';
    console.error('Error linking anonymous tracking:', error);
    return { success: false, error: errorMessage };
  }
}

